import React, { useState } from "react";
import { FaChevronLeft, FaChevronRight } from "react-icons/fa";
import { useNavigate } from "react-router-dom";

const bots = [
  {
    name: "XENOARC",
    role: "Trading bot",
    img: "/images/bots/xenoarc.png",
    description: `XENOARC leverages the GRID trading strategy to automate profit accumulation through market fluctuations. Ideal for the highly volatile crypto market, it operates 24/7, buying at price drops and selling at rises, ensuring consistent gains without leveraging risk. With its simplicity and efficiency, XENOARCempowers traders to maximize market opportunities effortlessly.`,
    isComingSoon: false,
  },
  {
    name: "DCA",
    role: "Trading bot",
    img: "/images/bots/dca.png",
    description:
      "The DCA bot spreads your investment over time with scheduled buys, helping reduce the impact of market volatility and avoid poor timing. Ideal for long-term accumulation and beginners.",
    isComingSoon: true,
  },
  {
    name: "GRID",
    role: "Trading bot",
    img: "/images/bots/grid.png",
    description:
      "The Rebalance bot dynamically adjusts your portfolio to maintain target asset ratios. It helps you stay diversified and aligned with your investment goals by reallocating funds as markets shift.",
    isComingSoon: false,
  },
  // {
  //   name: "Rebalancing",
  //   role: "Trading bot",
  //   img: "/images/bots/rebalancing.png",
  //   description:
  //     "The DCA bot spreads your investment over time with scheduled buys, helping reduce the impact of market volatility and avoid poor timing. Ideal for long-term accumulation and beginners.",
  // },
];

const TradingStrategy = ({ botRef }) => {
  const [activeIndex, setActiveIndex] = useState(0);

  const handlePrev = () => {
    setActiveIndex((prev) => (prev === 0 ? bots.length - 1 : prev - 1));
  };

  const handleNext = () => {
    setActiveIndex((prev) => (prev === bots.length - 1 ? 0 : prev + 1));
  };

  return (
    <section ref={botRef}>
      <div className="w-full max-w-[1320px] mx-auto px-5 pt-28">
        <h2 className="text-center text-[28px] text-[#21272A]">
          <strong className="block text-[36px]">
            Bespoke Strategies, Automated for You
          </strong>
          <br className="md:hidden" />
          Leverage on our trade bot to automate gains
        </h2>

        {/* --- MODIFICATION START --- */}
        {/* Set a fixed height on the container to prevent layout shifts during animation */}
        <div className="relative mt-10 md:mt-20 lg:mt-16 flex items-center justify-center max-w-[1100px] mx-auto h-[450px]">
          <button
            onClick={handlePrev}
            className="absolute left-0 top-1/2 -translate-y-1/2 z-20 text-white bg-[#302571] p-3 rounded-full hover:bg-opacity-80 transition-opacity"
          >
            <FaChevronLeft />
          </button>

          {/* This wrapper holds all the absolutely positioned cards */}
          <div className="relative w-full h-full flex items-center justify-center">
            {bots.map((bot, index) => (
              <CardBot
                key={bot.name}
                bot={bot}
                index={index}
                activeIndex={activeIndex}
                totalItems={bots.length}
              />
            ))}
          </div>

          <button
            onClick={handleNext}
            className="absolute right-0 top-1/2 -translate-y-1/2 z-20 text-white bg-[#302571] p-3 rounded-full hover:bg-opacity-80 transition-opacity"
          >
            <FaChevronRight />
          </button>
        </div>
        {/* --- MODIFICATION END --- */}
      </div>
    </section>
  );
};

export default TradingStrategy;

const CardBot = ({ bot, index, activeIndex, totalItems }) => {
  const navigate = useNavigate();

  // This logic remains the same
  let position = index - activeIndex;

  if (position > totalItems / 2) {
    position -= totalItems;
  } else if (position < -totalItems / 2) {
    position += totalItems;
  }

  // --- MODIFICATION START ---
  // Define styles with even smaller mobile-friendly values
  const getStyle = () => {
    // Active card (center)
    if (position === 0) {
      return {
        transform: "translateX(0) scale(1)",
        opacity: 1,
        zIndex: 10,
        filter: "blur(0px)",
      };
    }
    // Previous card (left)
    if (position === -1) {
      return {
        // Reduced scale further to make side cards appear smaller
        transform: "translateX(-35%) scale(0.75)",
        opacity: 0.5,
        zIndex: 5,
        filter: "blur(2px)",
      };
    }
    // Next card (right)
    if (position === 1) {
      return {
        // Reduced scale further to make side cards appear smaller
        transform: "translateX(35%) scale(0.75)",
        opacity: 0.5,
        zIndex: 5,
        filter: "blur(2px)",
      };
    }
    // Hidden cards
    return {
      transform: `translateX(${position > 0 ? 70 : -70}%) scale(0.5)`,
      opacity: 0,
      zIndex: 0,
      filter: "blur(4px)",
    };
  };
  // --- MODIFICATION END ---

  return (
    <div
      // --- MODIFICATION START ---
      // Further reduced width and padding for the smallest screens
      className={`absolute transition-all duration-500 ease-in-out bg-[#302571] p-3 sm:p-4 w-full sm:max-w-[340px] lg:max-w-[440px] rounded-2xl text-white ${
        index - activeIndex === 0 ? "max-w-[240px]" : "max-w-[210px]"
      }`}
      style={getStyle()}
      // --- MODIFICATION END ---
    >
      {/* image placeholder with smaller base height */}
      <div
        className="w-full flex items-center justify-center h-[160px] sm:h-[230px] rounded-[14px] mb-3 bg-cover bg-center"
        style={{
          backgroundImage:
            "url('/images/backgrounds/bg-trading-strat-card.png')",
        }}
      >
        <img src={bot.img} alt={bot.name} className="max-md:max-w-[160px]" />
      </div>

      <h4 className="text-lg sm:text-xl uppercase font-bold">{bot.name}</h4>
      <span>{bot.role}</span>
      <p className="text-xs sm:text-sm text-[#CECBE2] mt-2">
        {bot.description}
      </p>
      {bot.isComingSoon ? (
        <div className="mt-3 flex items-center gap-2">
          <span className="px-3 py-1 sm:px-6 sm:py-1.5 rounded-full !bg-[#939393]/55 font-bold w-fit text-xs sm:text-sm">
            Coming Soon
          </span>
        </div>
      ) : (
        <div className="mt-3 flex items-center gap-2">
          <button
            className="px-3 py-1 sm:px-4 sm:py-2 mt-3 rounded-full !bg-[#007A78] font-bold w-fit text-xs sm:text-sm"
            onClick={() => navigate("/signup")}
          >
            Try Bot
          </button>
          <button
            className="px-3 py-1 sm:px-4 sm:py-2 mt-3 rounded-full !bg-[#939393]/55 font-bold w-fit text-xs sm:text-sm"
            onClick={() => navigate(`/learn-more/${bot.name.toLowerCase()}`)}
          >
            Learn More
          </button>
        </div>
      )}

      {/* {bot.name === "XENOARC" && (
        <ul className="mt-3 flex items-center gap-2">
          <li className="px-3 py-1 sm:px-6 sm:py-1.5 rounded-full bg-[#007A78] font-bold w-fit text-xs sm:text-sm">
            Primary
          </li>
          <li className="px-3 py-1 sm:px-6 sm:py-1.5 rounded-full bg-[#939393] font-bold w-fit text-xs sm:text-sm">
            Primary
          </li>
        </ul>
      )} */}
    </div>
  );
};
